import { Component } from '@theme/component';

const STORAGE_KEY = 'geo-redirect-dismissed';
const DISMISS_DAYS = 30;

class GeoRedirectPopup extends Component {
  requiredRefs = ['dialog', 'text', 'storeName'];

  #redirectUrl = '';

  connectedCallback() {
    super.connectedCallback();
    this.#init();
    this.#listenForManualOpen();
  }

  async #init() {
    if (this.#wasDismissed()) return;
    await this.#detectAndShow();
  }

  /** Detect country, find matching store, populate text, and open the dialog. */
  async #detectAndShow() {
    const stores = this.#getStores();
    if (!stores.length) return;

    const country = await this.#detectCountry();
    if (!country) return;

    const match = stores.find((s) => s.countries.includes(country));
    if (!match) return;

    this.#redirectUrl = match.url;
    const flag = this.#countryFlag(country);

    this.refs.text.innerHTML = `It seems you're in ${flag} <strong>${this.#countryName(country)}</strong>. Redirect to`;
    this.refs.storeName.textContent = match.name;

    this.refs.dialog.showModal();
  }

  /** Listen for #ngr-open hash — allows footer "Country Select" link to open the popup. */
  #listenForManualOpen() {
    const openIfHash = () => {
      if (window.location.hash === '#ngr-open') {
        history.replaceState(null, '', window.location.pathname + window.location.search);
        this.#detectAndShow();
      }
    };

    // Check on load (in case the page was navigated to with the hash)
    openIfHash();

    // Listen for hash changes (in-page link clicks)
    window.addEventListener('hashchange', openIfHash);
  }

  #getStores() {
    try {
      return JSON.parse(this.dataset.stores || '[]');
    } catch {
      return [];
    }
  }

  async #detectCountry() {
    try {
      const res = await fetch('https://ipapi.co/country/');
      if (!res.ok) return null;
      const code = (await res.text()).trim();
      return code.length === 2 ? code : null;
    } catch {
      return null;
    }
  }

  #countryFlag(code) {
    const offset = 0x1f1e6;
    const a = code.codePointAt(0) - 65 + offset;
    const b = code.codePointAt(1) - 65 + offset;
    return String.fromCodePoint(a, b);
  }

  #countryName(code) {
    try {
      const names = new Intl.DisplayNames(['en'], { type: 'region' });
      return names.of(code) || code;
    } catch {
      return code;
    }
  }

  #wasDismissed() {
    try {
      const ts = localStorage.getItem(STORAGE_KEY);
      if (!ts) return false;
      const elapsed = Date.now() - Number(ts);
      return elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  }

  #saveDismissal() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // localStorage unavailable
    }
  }

  accept() {
    if (this.#redirectUrl) {
      this.#saveDismissal();
      window.location.href = this.#redirectUrl;
    }
  }

  dismiss() {
    this.#saveDismissal();
    this.refs.dialog.close();
  }
}

if (!customElements.get('geo-redirect-popup')) {
  customElements.define('geo-redirect-popup', GeoRedirectPopup);
}
